import { useEffect, useMemo, useState } from 'react';
import { CalendarClock, Landmark, RefreshCw, Send, Wallet } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { useAuthStore } from '../store/authStore';

type BankingEnvironment = 'SANDBOX' | 'PRODUCTION';

interface BankingConfigResponse {
  activeEnvironment: BankingEnvironment;
}

interface AccountOverview {
  environment: BankingEnvironment;
  setupRequired?: boolean;
  setupMessage?: string | null;
  statement: {
    totalIncoming: number;
    totalOutgoing: number;
    net: number;
  };
  c6pay: {
    receivables: { totalAmount: number };
  };
  activity: {
    recentWebhookEvents: Array<{ id: string; event?: string; createdAt: string }>;
    recentBankingAudits: Array<{ id: string; action?: string; createdAt: string }>;
  };
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    Number.isFinite(Number(value)) ? Number(value) : 0,
  );

const parseApiError = (error: any, fallback: string) =>
  error?.response?.data?.message || error?.response?.data?.details?.message || fallback;

export default function BankingPage() {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [sendingPix, setSendingPix] = useState(false);
  const [sendingScheduled, setSendingScheduled] = useState(false);

  const [overview, setOverview] = useState<AccountOverview | null>(null);

  const [dateRange, setDateRange] = useState(() => {
    const now = new Date();
    const start = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    const end = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
      now.getDate(),
    ).padStart(2, '0')}`;
    return { start, end };
  });

  const [reportEnv, setReportEnv] = useState<BankingEnvironment>('SANDBOX');

  const [pixForm, setPixForm] = useState({
    amount: '',
    recipientName: '',
    recipientDocument: '',
    pixKey: '',
    pixKeyType: 'EVP',
    category: 'COMMISSION',
    referenceId: '',
    description: '',
    providerPath: '/payments',
    paymentPin: '',
  });

  const [scheduledForm, setScheduledForm] = useState({
    amount: '',
    beneficiaryName: '',
    beneficiaryDocument: '',
    paymentDate: dateRange.end,
    category: 'EXTRA',
    referenceId: '',
    description: '',
    providerPath: '/',
    paymentPin: '',
  });

  const canCriticalPayments = useMemo(
    () => ['ADMIN', 'DIRETOR'].includes(user?.profile || ''),
    [user?.profile],
  );

  const loadOverview = async (environment: BankingEnvironment, startDate: string, endDate: string) => {
    const res = await api.get('/banking/overview', {
      params: { environment, startDate, endDate },
    });
    setOverview(res.data);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const cfgRes = await api.get('/banking/config');
      const cfg = cfgRes.data as BankingConfigResponse;
      const env = cfg.activeEnvironment || 'SANDBOX';
      setReportEnv(env);
      await loadOverview(env, dateRange.start, dateRange.end);
    } catch (error: any) {
      alert(parseApiError(error, 'Falha ao carregar Banking.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const refreshOverview = async () => {
    try {
      await loadOverview(reportEnv, dateRange.start, dateRange.end);
    } catch (error: any) {
      alert(parseApiError(error, 'Falha ao atualizar visao da conta.'));
    }
  };

  const sendPixPayment = async () => {
    try {
      setSendingPix(true);
      const res = await api.post('/banking/payments/pix', {
        amount: Number(pixForm.amount),
        recipientName: pixForm.recipientName,
        recipientDocument: pixForm.recipientDocument || undefined,
        pixKey: pixForm.pixKey,
        pixKeyType: pixForm.pixKeyType,
        category: pixForm.category,
        referenceId: pixForm.referenceId || undefined,
        description: pixForm.description || undefined,
        providerPath: pixForm.providerPath,
        paymentPin: pixForm.paymentPin,
        environment: reportEnv,
      });
      alert(`PIX enviado com sucesso. Status: ${res.data?.status || 'OK'}`);
      setPixForm((prev) => ({ ...prev, paymentPin: '' }));
      await refreshOverview();
    } catch (error: any) {
      alert(parseApiError(error, 'Falha ao enviar PIX.'));
    } finally {
      setSendingPix(false);
    }
  };

  const sendScheduledPayment = async () => {
    try {
      setSendingScheduled(true);
      const res = await api.post('/banking/payments/scheduled', {
        amount: Number(scheduledForm.amount),
        beneficiaryName: scheduledForm.beneficiaryName,
        beneficiaryDocument: scheduledForm.beneficiaryDocument,
        paymentDate: scheduledForm.paymentDate,
        category: scheduledForm.category,
        referenceId: scheduledForm.referenceId || undefined,
        description: scheduledForm.description || undefined,
        providerPath: scheduledForm.providerPath,
        paymentPin: scheduledForm.paymentPin,
        environment: reportEnv,
      });
      alert(`Pagamento agendado enviado. Status: ${res.data?.status || 'OK'}`);
      setScheduledForm((prev) => ({ ...prev, paymentPin: '' }));
      await refreshOverview();
    } catch (error: any) {
      alert(parseApiError(error, 'Falha ao agendar pagamento.'));
    } finally {
      setSendingScheduled(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Carregando Banking...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Landmark className="w-6 h-6" /> Banking
            </h1>
            <p className="text-gray-600 mt-1">Operacao da conta: PIX, pagamentos, saldo e atividade.</p>
          </div>
          <Link to="/bankconf" className="btn-secondary">Abrir BankConf (configuracao)</Link>
        </div>
      </div>

      <div className="card space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2"><Wallet className="w-5 h-5" /> Visao da conta</h2>
          <button className="btn-secondary flex items-center gap-2" onClick={refreshOverview}><RefreshCw className="w-4 h-4" /> Atualizar</button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div><label className="label">Ambiente</label><select className="input" value={reportEnv} onChange={(e) => setReportEnv(e.target.value as BankingEnvironment)}><option value="SANDBOX">Homologacao</option><option value="PRODUCTION">Producao</option></select></div>
          <div><label className="label">Data inicial</label><input className="input" type="date" value={dateRange.start} onChange={(e) => setDateRange((prev) => ({ ...prev, start: e.target.value }))} /></div>
          <div><label className="label">Data final</label><input className="input" type="date" value={dateRange.end} onChange={(e) => setDateRange((prev) => ({ ...prev, end: e.target.value }))} /></div>
        </div>

        {overview?.setupRequired && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <p className="font-semibold">Banking precisa de configuracao no BankConf</p>
            <p>{overview.setupMessage || 'Configure clientId/clientSecret e mTLS no BankConf.'}</p>
            <div className="mt-2"><Link to="/bankconf" className="underline">Ir para BankConf</Link></div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="rounded-lg border p-3"><p className="text-xs text-gray-500">Entradas</p><p className="font-semibold">{formatCurrency(overview?.statement.totalIncoming || 0)}</p></div>
          <div className="rounded-lg border p-3"><p className="text-xs text-gray-500">Saidas</p><p className="font-semibold">{formatCurrency(overview?.statement.totalOutgoing || 0)}</p></div>
          <div className="rounded-lg border p-3"><p className="text-xs text-gray-500">Saldo liquido</p><p className="font-semibold">{formatCurrency(overview?.statement.net || 0)}</p></div>
          <div className="rounded-lg border p-3"><p className="text-xs text-gray-500">Recebiveis C6Pay</p><p className="font-semibold">{formatCurrency(overview?.c6pay.receivables.totalAmount || 0)}</p></div>
        </div>
      </div>

      <div className="card space-y-4">
        <h2 className="text-lg font-semibold flex items-center gap-2"><Send className="w-5 h-5" /> PIX para comissoes e extras (PIN obrigatorio)</h2>
        {!canCriticalPayments && <p className="text-sm text-amber-600">Somente ADMIN e DIRETOR podem executar pagamentos PIX.</p>}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div><label className="label">Valor</label><input className="input" type="number" step="0.01" value={pixForm.amount} onChange={(e) => setPixForm((prev) => ({ ...prev, amount: e.target.value }))} /></div>
          <div><label className="label">Categoria</label><select className="input" value={pixForm.category} onChange={(e) => setPixForm((prev) => ({ ...prev, category: e.target.value }))}><option value="COMMISSION">Comissao</option><option value="EXTRA">Extra</option><option value="GENERAL">Geral</option></select></div>
          <div><label className="label">Nome destinatario</label><input className="input" value={pixForm.recipientName} onChange={(e) => setPixForm((prev) => ({ ...prev, recipientName: e.target.value }))} /></div>
          <div><label className="label">Chave PIX</label><input className="input" value={pixForm.pixKey} onChange={(e) => setPixForm((prev) => ({ ...prev, pixKey: e.target.value }))} /></div>
          <div><label className="label">Documento</label><input className="input" value={pixForm.recipientDocument} onChange={(e) => setPixForm((prev) => ({ ...prev, recipientDocument: e.target.value }))} /></div>
          <div><label className="label">Tipo chave</label><select className="input" value={pixForm.pixKeyType} onChange={(e) => setPixForm((prev) => ({ ...prev, pixKeyType: e.target.value }))}><option value="EVP">EVP</option><option value="EMAIL">EMAIL</option><option value="PHONE">PHONE</option><option value="CPF">CPF</option><option value="CNPJ">CNPJ</option></select></div>
          <div><label className="label">Referencia</label><input className="input" value={pixForm.referenceId} onChange={(e) => setPixForm((prev) => ({ ...prev, referenceId: e.target.value }))} /></div>
          <div><label className="label">PIN (6 digitos)</label><input className="input" type="password" maxLength={6} value={pixForm.paymentPin} onChange={(e) => setPixForm((prev) => ({ ...prev, paymentPin: e.target.value.replace(/\D/g, '') }))} /></div>
        </div>
        <div><label className="label">Descricao</label><input className="input" value={pixForm.description} onChange={(e) => setPixForm((prev) => ({ ...prev, description: e.target.value }))} /></div>
        <div className="flex justify-end"><button className="btn-primary" onClick={sendPixPayment} disabled={!canCriticalPayments || sendingPix}>{sendingPix ? 'Enviando...' : 'Enviar PIX'}</button></div>
      </div>

      <div className="card space-y-4">
        <h2 className="text-lg font-semibold flex items-center gap-2"><CalendarClock className="w-5 h-5" /> Pagamento agendado (comissao/extras)</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div><label className="label">Valor</label><input className="input" type="number" step="0.01" value={scheduledForm.amount} onChange={(e) => setScheduledForm((prev) => ({ ...prev, amount: e.target.value }))} /></div>
          <div><label className="label">Data</label><input className="input" type="date" value={scheduledForm.paymentDate} onChange={(e) => setScheduledForm((prev) => ({ ...prev, paymentDate: e.target.value }))} /></div>
          <div><label className="label">Categoria</label><select className="input" value={scheduledForm.category} onChange={(e) => setScheduledForm((prev) => ({ ...prev, category: e.target.value }))}><option value="COMMISSION">Comissao</option><option value="EXTRA">Extra</option><option value="GENERAL">Geral</option></select></div>
          <div><label className="label">PIN</label><input className="input" type="password" maxLength={6} value={scheduledForm.paymentPin} onChange={(e) => setScheduledForm((prev) => ({ ...prev, paymentPin: e.target.value.replace(/\D/g, '') }))} /></div>
          <div><label className="label">Beneficiario</label><input className="input" value={scheduledForm.beneficiaryName} onChange={(e) => setScheduledForm((prev) => ({ ...prev, beneficiaryName: e.target.value }))} /></div>
          <div><label className="label">Documento</label><input className="input" value={scheduledForm.beneficiaryDocument} onChange={(e) => setScheduledForm((prev) => ({ ...prev, beneficiaryDocument: e.target.value }))} /></div>
          <div><label className="label">Referencia</label><input className="input" value={scheduledForm.referenceId} onChange={(e) => setScheduledForm((prev) => ({ ...prev, referenceId: e.target.value }))} /></div>
          <div><label className="label">Path provedor</label><input className="input" value={scheduledForm.providerPath} onChange={(e) => setScheduledForm((prev) => ({ ...prev, providerPath: e.target.value }))} /></div>
        </div>
        <div><label className="label">Descricao</label><input className="input" value={scheduledForm.description} onChange={(e) => setScheduledForm((prev) => ({ ...prev, description: e.target.value }))} /></div>
        <div className="flex justify-end"><button className="btn-primary" onClick={sendScheduledPayment} disabled={!canCriticalPayments || sendingScheduled}>{sendingScheduled ? 'Enviando...' : 'Agendar pagamento'}</button></div>
      </div>
    </div>
  );
}
